'use client';

import { DE_LOCALE } from '@core/Constants/Locale.constants';
import { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { VehicleSpec } from '@core/Entities/Vehicle/VehicleSpec.entity';
import { getPsFromKw } from '@core/Utils/SearchFilters/GetPsFromKw';
import { Pill } from '@gds/Pill/Pill';

const FILTER_PRIORITY = ['fuel-type', 'body-type', 'gear-box'] as const;
const FILTER_PRIORITY_DE = ['fuel-type', 'engine-power', 'gear-box'] as const;

const baseFilterToSpecMap: Record<string, string> = {
  'fuel-type': 'fuelType',
  'body-type': 'bodyType',
  'gear-box': 'gearbox',
};

const baseFilterToSpecMapDE: Record<string, string | string[]> = {
  'fuel-type': 'fuelType',
  'engine-power': ['engine-power__gte', 'engine-power__lte'],
  'gear-box': 'gearbox',
};

const DynamicVehicleSpec = ({
  vehicleSpec,
  strings,
  activeFilters,
  locale,
}: {
  vehicleSpec: VehicleSpec;
  strings: Record<string, string>;
  activeFilters: Filter[];
  locale: string;
}) => {
  const isDe = locale === DE_LOCALE;

  const filterPriority = isDe ? FILTER_PRIORITY_DE : FILTER_PRIORITY;
  const filterToSpecMap = isDe ? baseFilterToSpecMapDE : baseFilterToSpecMap;

  let activeFilter: string | undefined;

  for (const filter of filterPriority) {
    const specKeyOrKeys = filterToSpecMap[filter];
    const idsToCheck = Array.isArray(specKeyOrKeys) ? specKeyOrKeys : [filter];

    const hasActiveValue = activeFilters.some(
      f => idsToCheck.includes(f.id) && f.value?.toString().trim(),
    );

    if (!hasActiveValue) {
      activeFilter = filter;
      break;
    }
  }

  if (!activeFilter) {
    activeFilter = filterPriority[0];
  }

  const specKeyOrKeys = filterToSpecMap[activeFilter];
  let pillLabel;

  if (activeFilter === 'engine-power') {
    if (vehicleSpec?.bhp && vehicleSpec?.bhp > 0) {
      pillLabel = `${getPsFromKw(vehicleSpec.bhp)} PS`;
    }
  } else {
    pillLabel = vehicleSpec[specKeyOrKeys as keyof VehicleSpec] as string;
  }

  if (!pillLabel) return null;

  return <Pill variant="vehicle-spec">{strings[pillLabel as string] || pillLabel}</Pill>;
};

export default DynamicVehicleSpec;
